import { useCallback, useEffect, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { captureError } from "src/monitoring/sentry";
import { ApiResponse } from "src/business/api";
import { Asset } from "src/business/restaurant/gallery/asset";
import {
  PaymentMethod,
  RestaurantType,
} from "src/business/restaurant/restaurant";
import {
  mapReadResponseToInfoDetails,
  mapReadResponseToScheduleDetails,
  mapRestaurantResponseToRestaurantType,
} from "src/data/restaurant/mappers/restaurantMapper";
import {
  RestaurantListResponseData,
  ScheduleEntryPayload,
} from "src/data/types/restaurant";
import { API_URL, getVersionedUrl } from "src/Utils/API";

export interface RestaurantUpdateRequest {
  name?: string;
  cityName?: string;
  address?: string;
  quartier?: string;
  phone?: string;
  paymentMethods?: PaymentMethod;
  numberOfSeats?: number;
  servesAlcohol?: boolean;
  hasAnimation?: boolean;
  instaTag?: string;
  tagIds?: string[];
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  schedule?: ScheduleEntryPayload[];
  bookingDuration?: number;
  maximumPartySize?: number;
}

export const restaurantKeys = {
  list: ["restaurants", "list"] as const,
  info: (restaurantId?: string) =>
    ["restaurants", restaurantId, "info"] as const,
  tableSchedule: (restaurantId?: string) =>
    ["restaurants", restaurantId, "table-schedule"] as const,
  openingHours: (restaurantId?: string) =>
    ["restaurants", restaurantId, "opening-hours"] as const,
};

/**
 * Re-reads everything a restaurant write can have changed: the three per-gate
 * read models, and the shared list row, which still carries the profile and the
 * slot length for the surfaces that only display them (sidebar picker,
 * dashboard, admin tables).
 *
 * Listed key by key rather than invalidated by the `["restaurants", id]` prefix
 * on purpose — that prefix also covers the read-access check, which is cached
 * for five minutes deliberately and is not affected by a write.
 */
const useInvalidateRestaurant = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useCallback(() => {
    [
      restaurantKeys.info(restaurantId),
      restaurantKeys.tableSchedule(restaurantId),
      restaurantKeys.openingHours(restaurantId),
      restaurantKeys.list,
    ].forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
  }, [queryClient, restaurantId]);
};

// Stable identity for "no restaurants", so an effect keyed on the list does not
// re-run on every render while the account holds none.
const NO_RESTAURANTS: RestaurantType[] = [];

/**
 * The restaurants the caller manages: admins get all of them, staff only the
 * ones assigned to them *that still hold an active subscription* — the backend
 * filters on it, so a restaurant whose subscription lapsed is simply absent.
 *
 * Fetched once on mount and cached under `restaurantKeys.list`. `loaded` is what
 * separates "the request has not answered yet" from "this account manages none",
 * which is a legitimate resting state (an owner who unsubscribed their last
 * restaurant) and must never be treated as a failure to retry: refetching an
 * empty list only ever returns an empty list.
 */
export const useGetRestaurantList = () => {
  const query = useQuery({
    queryKey: restaurantKeys.list,
    queryFn: async (): Promise<RestaurantType[]> => {
      try {
        const res = await fetch(`${API_URL}/restaurants`, {
          credentials: "include",
        });
        const body: RestaurantListResponseData = await res.json();
        if (!body.success) {
          throw new Error(body.error ?? "Failed to fetch the restaurant list");
        }
        return body.restaurants.map(mapRestaurantResponseToRestaurantType);
      } catch (error) {
        captureError(error);
        throw error;
      }
    },
    // One attempt, one report: a refusal here (signed out, no access) is an
    // answer, not a transient failure worth hammering the endpoint over.
    retry: false,
  });

  return {
    refetch: query.refetch,
    loading: query.isPending,
    loaded: query.isSuccess,
    restaurantList: query.data ?? NO_RESTAURANTS,
  };
};

type RestaurantDetailsResource<TDetails> = {
  /** Re-reads now. Awaitable, so a caller can hold its saving state until the new values are on screen. */
  getRestaurantDetails: () => Promise<unknown>;
  restaurantDetails: TDetails | undefined;
  /**
   * First read only — nothing is on screen yet, so callers swap in a spinner.
   * Stays false while a re-read runs behind an already-rendered page, which is
   * what keeps a post-save invalidation from flashing the page away.
   */
  loading: boolean;
  /**
   * A read is in flight, first or not. For callers that must disable an action
   * until the value it depends on has landed.
   */
  refreshing: boolean;
  /**
   * The request was refused or failed. Reported to the caller instead of being
   * turned into a redirect: the read model sits behind a per-feature permission,
   * so a refusal here is a reason to explain the restriction on the page the user
   * asked for, not to bounce them to another one.
   */
  error: boolean;
};

/**
 * Loads a restaurant read model into the query cache under `queryKey`.
 *
 * Each gate gets its own key so a mutation can invalidate exactly the reads it
 * affected. The hooks below differ in three ways — the path, the key, and the
 * mapper — because the three endpoints no longer return the same payload: the
 * profile is served only through the info gate, the slot length only through
 * the two schedule gates.
 */
const useRestaurantDetailsResource = <TResponse, TDetails>(
  queryKey: readonly unknown[],
  buildPath: (restaurantId: string) => string,
  mapResponse: (response: TResponse) => TDetails,
  restaurantId?: string,
): RestaurantDetailsResource<TDetails> => {
  const query = useQuery({
    queryKey,
    queryFn: async (): Promise<TDetails> => {
      try {
        const response = await fetch(buildPath(restaurantId as string), {
          credentials: "include",
        });
        const data: ApiResponse & TResponse = await response.json();
        if (!data.success) {
          throw new Error("Failed to fetch restaurant details");
        }
        return mapResponse(data);
      } catch (error) {
        captureError(error);
        throw error;
      }
    },
    enabled: !!restaurantId,
    // A refusal here is an answer — the caller renders the restriction — not a
    // transient failure worth hammering a permission-gated endpoint over.
    retry: false,
  });

  return {
    getRestaurantDetails: query.refetch,
    restaurantDetails: query.data,
    // An absent id never resolves, so report the disabled query as loading
    // rather than as an instant miss.
    loading: query.isPending,
    refreshing: query.isFetching,
    error: query.isError,
  };
};

const restaurantInfoPath = (restaurantId: string) =>
  `${API_URL}/restaurants/${restaurantId}`;

const tableScheduleReadPath = (restaurantId: string) =>
  `${API_URL}/restaurants/${restaurantId}/table-schedule`;

const openingHoursReadPath = (restaurantId: string) =>
  `${API_URL}/restaurants/${restaurantId}/opening-hours`;

/**
 * Gated on restaurant_management + restaurant_information_read. Carries the
 * editable profile — the restaurant-information page reads it here rather than
 * from the shared list row, so a save invalidates a query that page owns.
 */
export const useGetRestaurantDetails = (restaurantId?: string) =>
  useRestaurantDetailsResource(
    restaurantKeys.info(restaurantId),
    restaurantInfoPath,
    mapReadResponseToInfoDetails,
    restaurantId,
  );

/**
 * Gated on table_scheduling + table_schedule_read. The board needs the table
 * count, booking duration, opening hours and leaves, and the staff who run it
 * are not required to hold the restaurant-information permissions — so this
 * payload carries no profile.
 */
export const useGetTableScheduleDetails = (restaurantId?: string) =>
  useRestaurantDetailsResource(
    restaurantKeys.tableSchedule(restaurantId),
    tableScheduleReadPath,
    mapReadResponseToScheduleDetails,
    restaurantId,
  );

/**
 * Gated on opening_hours_management + opening_hours_read. The opening-hours
 * page needs the schedule, leaves and booking duration, and the staff who
 * manage them are not required to hold the restaurant-information permissions —
 * so this payload carries no profile either.
 */
export const useGetOpeningHoursDetails = (restaurantId?: string) =>
  useRestaurantDetailsResource(
    restaurantKeys.openingHours(restaurantId),
    openingHoursReadPath,
    mapReadResponseToScheduleDetails,
    restaurantId,
  );

/**
 * Writes any subset of the restaurant's editable fields.
 *
 * On success every read of this restaurant is invalidated, so the page that
 * saved re-reads its own query rather than mirroring the saved value in local
 * state until the next full page load.
 */
export const useUpdateRestaurant = (restaurantId: string) => {
  const [updating, setUpdating] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(false);
  const invalidateRestaurant = useInvalidateRestaurant(restaurantId);

  const updateRestaurant = useCallback(
    (updateData: RestaurantUpdateRequest) => {
      setUpdating(true);
      setUpdateSuccess(false);
      setUpdateError(false);

      return fetch(`${API_URL}/restaurants/${restaurantId}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(updateData),
      })
        .then((response) => response.json())
        .then((data: ApiResponse) => {
          if (data.success) {
            setUpdateSuccess(true);
            invalidateRestaurant();
            return data;
          } else {
            setUpdateError(true);
            throw new Error("Failed to update restaurant");
          }
        })
        .catch((error) => {
          console.error("Error updating restaurant:", error);
          setUpdateError(true);
          throw error;
        })
        .finally(() => {
          setUpdating(false);
        });
    },
    [restaurantId, invalidateRestaurant],
  );

  const resetUpdateStatus = useCallback(() => {
    setUpdateSuccess(false);
    setUpdateError(false);
  }, []);

  return {
    updateRestaurant,
    updating,
    updateSuccess,
    updateError,
    resetUpdateStatus,
  };
};

/**
 * Dedicated endpoint to set/update a restaurant's number of bookable tables.
 * Callers should refetch the restaurant details on success so the table-schedule
 * board picks up the new value.
 */
export const useUpdateNumberOfTables = (
  restaurantId: string,
  onSuccess?: () => void,
) =>
  useMutation({
    mutationFn: async (numberOfTables: number) => {
      const response = await fetch(
        `${API_URL}/restaurants/${restaurantId}/number-of-tables`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ numberOfTables }),
        },
      );
      const data: ApiResponse = await response.json();
      if (!data.success) {
        throw new Error("Failed to update number of tables");
      }
      return data;
    },
    onSuccess,
  });

export const useGetRestaurantGallery = (restaurantId?: string) => {
  const [images, setImages] = useState<Asset[]>([]);
  const [galleryImages, setGalleryImages] = useState<Asset[]>([]);
  const [menuImages, setMenuImages] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);

  const getRestaurantGallery = useCallback(() => {
    if (!restaurantId) return;

    setLoading(true);
    fetch(`${getVersionedUrl()}/restaurants/${restaurantId}/gallery`)
      .then((res) => res.json())
      .then((response: Asset[]) => {
        setGalleryImages(
          response.filter((image) => image.imageType === "GALLERY"),
        );
        setMenuImages(response.filter((image) => image.imageType === "MENU"));
        setImages(response);
      })
      .catch((e) => captureError(e))
      .finally(() => setLoading(false));
  }, [restaurantId]);

  useEffect(() => {
    getRestaurantGallery();
  }, [getRestaurantGallery]);

  return { images, getRestaurantGallery, loading, galleryImages, menuImages };
};

const RESTAURANT_READ_ACCESS_STALE_MS = 5 * 60 * 1000;

export const useRestaurantReadAccess = (
  restaurantId: string | undefined,
  enabled: boolean,
) => {
  return useQuery({
    queryKey: ["restaurants", restaurantId, "read-access"],
    queryFn: async () => {
      const response = await fetch(
        `${API_URL}/restaurants/${restaurantId}/read-access`,
        { credentials: "include" },
      );
      return response.ok;
    },
    enabled: !!restaurantId && enabled,
    staleTime: RESTAURANT_READ_ACCESS_STALE_MS,
    gcTime: RESTAURANT_READ_ACCESS_STALE_MS,
  });
};
